import HttpClient from './http';
import devmode from './env';
import { delay } from 'components/misc';

class Backend {
    constructor() {
        this.host_url = (devmode()) ? "http://127.0.0.1:8080" : window.location.origin;
        this.client = new HttpClient(this.host_url);

    }

    async renewToken() {
        for (let i = 0; i < 5; i++) {
            const token = window.localStorage.getItem("token");
            const refresh_token = window.localStorage.getItem("refresh_token");
            if (token != null) {
                const result = await this.client.post('/api/account/token', {refresh_token: refresh_token}, {"X-Token": token});
                if (result.error == null) {
                    window.localStorage.setItem("token", result.response.token);
                    window.localStorage.setItem("refresh_token", result.response.refresh_token);
                    return;
                }
                await delay(100);
            }
        }
    }

    updateTokenFromResult(result) {
        if (result.error == null) {
            window.localStorage.setItem("token", result.response.token);
            window.localStorage.setItem("refresh_token", result.response.refresh_token);
        } else {
            window.localStorage.removeItem("token");
            window.localStorage.removeItem("refresh_token");
        }
    }

    async health() {
        return await this.client.get('/api/health', {});
    }

    async signin(data) {
        const result = await this.client.post('/api/account/signin', data, {});
        this.updateTokenFromResult(result);
        return result;
    }

    async signup(data) {
        return await this.client.post('/api/account/signup', data, {});
    }

    async signout() {
        const token = window.localStorage.getItem("token");
        if (token != null) {
            const result = await this.client.post('/api/account/signout', {}, {"X-Token": token});
            if (result.error == null) {
                window.localStorage.removeItem("token");
                window.localStorage.removeItem("refresh_token");
            }
            return result;
        }
        return {response: {auth_type: "", account_id: ""}, error: null};
    }

    async profile() {
        const token = window.localStorage.getItem("token");
        if (token != null) {
            return await this.client.get('/api/account/profile', {"X-Token": token});
        } else {
            return {response: null, error: "no token"};
        }
    }

    async verifyEmail(data) {
        return await this.client.post('/api/account/verify-email', data, {});
    }

    async deleteAccount(data) {
        const token = window.localStorage.getItem("token");
        const result = await this.client.post('/api/account/delete', {}, {"X-Token": token});
        if (result.error == null) {
            window.localStorage.removeItem("token");
            window.localStorage.removeItem("refresh_token");
        }
        return result;
    }

    async getBillingInfo() {
        const token = window.localStorage.getItem("token");
        return await this.client.get('/api/billing/billing-info', {"X-Token": token});
    }

    async setBillingSession(session_id) {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/billing/billing-session', { session_id: session_id}, {"X-Token": token});
    }

    async updateBillingSubscription(price_lookup_key) {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/billing/update-subscription', {price_lookup_key: price_lookup_key}, {"X-Token": token});
    }

    async getNotifications(offset, limit) {
        const token = window.localStorage.getItem("token");
        return await this.client.get(`/api/notifications?offset=${offset}&limit=${limit}`, {"X-Token": token});
    }

    async getUnreadNotifications(offset, limit) {
        const token = window.localStorage.getItem("token");
        return await this.client.get(`/api/notifications/unread?offset=${offset}&limit=${limit}`, {"X-Token": token});
    }

    async markNotificationRead(id) {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/notifications/mark-read', {id: id}, {"X-Token": token});
    }

    async deleteNotification(id) {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/notifications/delete', {id: id}, {"X-Token": token});
    }

    async getSettings() {
        const token = window.localStorage.getItem("token");
        return await this.client.get('/api/settings', {"X-Token": token});
    }

    async setSettings(settings) {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/settings', settings, {"X-Token": token});
    }

    async getBlogPosts() {
        return await this.client.get('/api/blog');
    }

    async getBlogPost(id) {
        return await this.client.get(`/api/blog/${id}`);
    }

    async getWorkers() {
        const token = window.localStorage.getItem("token");
        return await this.client.get('/api/worker/list', {"X-Token": token});
    }

    async getWorker(id) {
        const token = window.localStorage.getItem("token");
        return await this.client.get(`/api/worker/${id}`, {"X-Token": token});
    }

    async deleteWorker(id) {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/worker/delete', {id: id}, {"X-Token": token});
    }

    async chatCompletions(api_key, model, messages) {
        const token = window.localStorage.getItem("token");

        const request = {
            model: model,
            messages: messages.map(msg => ({
                role: msg.role,
                content: msg.content
            }))
        };

        return await this.client.post('/api/llm/v1/chat/completions',
            request,
            {"X-Token": token, "Authorization": "Bearer " + api_key}
        );
    }

    async createApiToken() {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/api-token/create', {}, {"X-Token": token});
    }

    async deleteApiToken(value) {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/api-token/delete', {token: value}, {"X-Token": token});
    }

    async listApiTokens() {
        const token = window.localStorage.getItem("token");
        return await this.client.get('/api/api-token/list', {"X-Token": token});
    }

    async postWorkerConfig(config) {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/worker/config', {config: config}, {"X-Token": token});
    }

    async getWorkerLogs(id, from, to) {
        const token = window.localStorage.getItem("token");
        return await this.client.get(`/api/worker/${id}/logs?from=${from}&to=${to}`, {"X-Token": token});
    }

    async createDataIndex(fileSystemPath) {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/data-index/create', {file_system_path: fileSystemPath}, {"X-Token": token});
    }

    async deleteDataIndex(id) {
        const token = window.localStorage.getItem("token");
        return await this.client.post('/api/data-index/delete', {id: id}, {"X-Token": token});
    }

    async getDataIndexes() {
        const token = window.localStorage.getItem("token");
        return await this.client.get('/api/data-index/list', {"X-Token": token});
    }

    async getDataIndex(id) {
        const token = window.localStorage.getItem("token");
        return await this.client.get(`/api/data-index/${id}`, {"X-Token": token});
    }

    async updateDataIndex(id, data) {
        const token = window.localStorage.getItem("token");
        return await this.client.post(`/api/data-index/${id}`, data, {"X-Token": token});
    }

    async chatDataIndexCompletions(api_key, model, dataIndexId, messages) {
        const token = window.localStorage.getItem("token");

        const request = {
            model: model,
            messages: messages.map(msg => ({
                role: msg.role,
                content: msg.content
            }))
        };

        return await this.client.post(`/api/data-index/${dataIndexId}/chat`,
            request,
            {"X-Token": token, "Authorization": "Bearer " + api_key}
        );
    }
}

var backend = null;

function getBackend() {
    if (backend != null)
        return backend;

    backend = new Backend();
    return backend;
}

export default getBackend;
